import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import { Link } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { login, error: authError, loading } = useAuth();

  const emailInputRef = useRef<HTMLInputElement>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Set focus on email field when component mounts
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    
    if (!username.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!password.trim()) {
      setPasswordError('Password is required');
      return;
    }

    const success = await login(username.trim(), password);
    
    if (success) {
      setUsername('');
      setPassword('');
      // Perform a physical redirect to the welcome page
      window.location.href = '/welcome';
    } else {
      setError('Login failed – check your email or password and try again.');
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start', 
      minHeight: 'calc(100vh - 400px)',
      paddingTop: '120px'
    }}>
      <div style={{ width: '100%', maxWidth: '400px', padding: '2rem', backgroundColor: 'var(--white)', borderRadius: '0.5rem', boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Login to AskCEO.ai</h2>
        <p style={{ marginBottom: '1.5rem', color: '#555', fontSize: '0.9rem' }}>
          Sign in to access your AI-powered business assistant and real-time location insights.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', color: '#333' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="Enter your email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                marginBottom: emailError ? '0.25rem' : '1rem', 
                border: emailError ? '1px solid red' : '1px solid #ccc', 
                borderRadius: '0.25rem' 
              }}
              ref={emailInputRef}
              required
            />
            {emailError && (
              <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                {emailError}
              </p>
            )}
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', color: '#333' }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                marginBottom: passwordError ? '0.25rem' : '1rem', 
                border: passwordError ? '1px solid red' : '1px solid #ccc', 
                borderRadius: '0.25rem' 
              }}
              required
            />
            {passwordError && (
              <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                {passwordError}
              </p>
            )}
          </div>
          {(error || authError) && (
            <p style={{ color: 'red', marginBottom: '1rem', textAlign: 'left' }}>
              {error || authError}
            </p>
          )}
          <Button 
            type="submit"
            disabled={loading}
            onClick={() => {
              // This handler is needed for proper button functionality
              // The actual login logic is handled by the form's onSubmit
              // This is intentionally empty as it defers to the form submission
            }}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </Button>
          <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
            <p>
              Don't have an account? <Link to="/signup" style={{ color: '#0066cc', textDecoration: 'none' }}>Sign up here</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
